import type { Scale } from '@tunasong/schemas'
import type { Chord } from '../chord/index.js'
import ScaleLib from '../scale/scale.js'
import { getDiatonicFunction } from './diatonic.js'

/**
 * Window-level cadence verdict over the last 2 chords of a chord sequence.
 *
 * Phase-1 v1 design constraints (Bowie + spec):
 *   - Cadence labels v1 NEVER promise PAC. `perfect` is hard-coded `false` for
 *     authentic — soprano-voice tracking ships in Phase 2+. The lexicon copy
 *     for `cadence-authentic` already hedges accordingly.
 *   - Inversion-blind: v1 does not distinguish root-position from inverted
 *     V/I. Half cadence accepts V in any position. PAC vs IAC defers entirely.
 *   - Phrase boundary in v1 = end-of-line (Joni's UX spec §2.1). Plagal and
 *     half cadences REQUIRE `phraseBoundary === true`; authentic and deceptive
 *     also require it (the spec gates all cadence rendering on the phrase
 *     boundary so the closing chip never appears mid-line).
 *   - Modal suppression (critical): in modes without a diatonic leading tone
 *     (Mixolydian, Dorian, Phrygian, Aeolian-as-mode, Locrian) the V→I motion
 *     is a step, not a dominant resolution. Suppress the authentic / deceptive
 *     labels there.
 *   - Minor exemption: `scale.type === 'minor'` (natural minor as a key, not
 *     a mode) IS exempt — the harmonic-minor raised 7 is conventional in
 *     practice, so V→i is genuinely authentic. `getDiatonicFunction` now
 *     labels major V in natural minor as `'V'` directly (the
 *     harmonic-minor-dominant fallback in diatonic.ts), so this file's
 *     pattern matching can rely on the function label alone — the prior
 *     `isMajorVInMinorKey` root-fifth workaround was removed in this fix.
 *
 * The output id always references a lexicon entry; surface components pull
 * level-appropriate copy via `useTheoryCopy` — never inline strings.
 */
export interface CadenceTag {
  kind: 'authentic' | 'plagal' | 'half' | 'deceptive'
  /** Hard-coded `false` for authentic in v1 (no soprano tracking). Absent for other kinds. */
  perfect?: false
  /** Indices into the input chord window for the chord pair forming the cadence. */
  span: [number, number]
  /** Lexicon entry id; never raw user-facing copy. */
  lexiconId: string
}

const LEXICON_IDS = {
  authentic: 'cadence-authentic',
  plagal: 'cadence-plagal',
  half: 'cadence-half',
  deceptive: 'cadence-deceptive',
} as const

const INTERVAL_INDEX_TO_DEGREE = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII'] as const

/**
 * Read interval-aware: true when the scale's natural form of `degree` is
 * flatted relative to the major scale. Mirrors `scaleHasFlatDegree` in
 * `diatonic.ts` (kept local to avoid an internal-export churn in this
 * milestone).
 */
const scaleHasFlatDegree = (
  scale: Scale,
  degree: 'II' | 'III' | 'V' | 'VI' | 'VII'
): boolean => {
  const { intervals } = ScaleLib.getScaleSpec(scale)
  const index = INTERVAL_INDEX_TO_DEGREE.indexOf(degree)
  return intervals[index] === `b${degree}`
}

/**
 * True when the scale has no leading tone (its 7th is ♭7). Mixolydian, Dorian,
 * Phrygian, Aeolian-as-mode, and Locrian all qualify. The natural-minor KEY
 * (`scale.type === 'minor'`) is intentionally exempt: in practice writers
 * raise the 7 on V (harmonic-minor color) and the spec accepts V→i in minor
 * as authentic. Modal Aeolian (`scale.type === 'aeolian'`) does NOT get the
 * exemption — keys imply harmonic-minor lift, modes imply purity of the
 * intervals.
 *
 * Exported for Cadence Lab's mode-suppression rule (Phase 2 Tier 1) so the
 * lab and detector share one source of truth.
 */
export const hasNoLeadingTone = (scale: Scale): boolean => {
  if (scale.type === 'minor') {
    return false
  }
  return scaleHasFlatDegree(scale, 'VII')
}

/**
 * Detect a cadence over the last two chords of `chords`.
 *
 * Returns `null` (rather than a non-cadence) when:
 *   - `scale` is undefined,
 *   - the window has fewer than 2 chords,
 *   - `phraseBoundary` is false (v1 gates all cadence rendering on end-of-line),
 *   - the scale is a leading-tone-free mode and the pattern is authentic /
 *     deceptive (modal suppression), or
 *   - no pattern matches.
 *
 * The minor-key V→i case is handled by `getDiatonicFunction`'s
 * harmonic-minor-dominant fallback, which labels major V in natural minor as
 * `'V'` directly. The previous root-fifth workaround in this file was
 * superseded by that fix.
 */
export const detectCadence = (
  chords: Chord[],
  scale: Scale | undefined,
  phraseBoundary: boolean
): CadenceTag | null => {
  if (!scale || chords.length < 2 || !phraseBoundary) {
    return null
  }
  const lastIdx = chords.length - 1
  const penult = chords[lastIdx - 1]
  const last = chords[lastIdx]
  const fnPenult = getDiatonicFunction(penult, scale)
  const fnLast = getDiatonicFunction(last, scale)
  const modalNoLT = hasNoLeadingTone(scale)

  // Authentic: V→I (major) or V→i (minor). Modal-suppression applies.
  const penultIsV = !modalNoLT && fnPenult === 'V'
  if (penultIsV && (fnLast === 'I' || fnLast === 'i')) {
    return { kind: 'authentic', perfect: false, span: [lastIdx - 1, lastIdx], lexiconId: LEXICON_IDS.authentic }
  }

  // Deceptive: V→vi (major key) or V→VI (minor key — diatonic VI is the chord
  // built on the natural-minor 6th, labeled '♭VI' by the analyzer relative to
  // the parallel major; in natural minor that IS the diatonic vi-substitute
  // landing target).
  if (penultIsV && (fnLast === 'vi' || fnLast === '♭VI')) {
    return { kind: 'deceptive', span: [lastIdx - 1, lastIdx], lexiconId: LEXICON_IDS.deceptive }
  }

  // Plagal: IV→I. Requires phraseBoundary (already gated above).
  if (fnPenult === 'IV' && fnLast === 'I') {
    return { kind: 'plagal', span: [lastIdx - 1, lastIdx], lexiconId: LEXICON_IDS.plagal }
  }

  // Half: ANY → V at a phrase boundary. Modal-suppression does not apply —
  // landing on a V chord at end-of-line still reads as "open" even in modes.
  if (fnLast === 'V') {
    return { kind: 'half', span: [lastIdx, lastIdx], lexiconId: LEXICON_IDS.half }
  }

  return null
}
